import { useQuery } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';

/**
 * React Query hook to fetch published job post detail data
 *
 * Fetches published job post data from the posting service API
 * and transforms it to a format suitable for viewing job details.
 * Unlike useGetDraftJobPost, this fetches fully published posts
 * with all required fields populated.
 *
 * @param postId - The unique identifier for the published job post
 * @returns React Query result object with job post detail data and query states
 *
 * @example
 * ```tsx
 * function JobDetailsPage() {
 *   const { id } = useParams();
 *   const { data, isLoading, error } = useGetJobPostDetail(id);
 *
 *   if (isLoading) return <CustomSpinner isLoading />;
 *   if (error) return <ErrorShower />;
 *
 *   return <JobDetailView jobPost={data} />;
 * }
 * ```
 */
export function useGetJobPostDetail(postId?: string) {
  const query = useQuery({
    queryKey: ['GET_JOB_POSTING_DETAIL', postId],
    queryFn: () => PermanentJobsService.getJobPostDetail(postId!),
    enabled: !!postId,
    refetchOnWindowFocus: false,
  });

  const { data, isLoading, isFetching } = query;

  return {
    ...query,
    data: data?.data,
    isInitialLoading: isLoading,
    isLoading: isLoading || isFetching,
  };
}
